/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { BookOpen, FileText, MessageCircle, Send, ShoppingBag, User } from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useBranding } from '@/lib/branding';

/** M28 «Мобильный Юрист»: устанавливаемое веб-приложение (PWA) для граждан по адресу /app */
const KEY = 'kd_app_token';
const tok = { get: () => { try { return localStorage.getItem(KEY); } catch { return null; } }, set: (v: string | null) => { try { v ? localStorage.setItem(KEY, v) : localStorage.removeItem(KEY); } catch { /* приватный режим */ } } };

async function call<T = any>(path: string, opt: { method?: string; body?: any } = {}): Promise<T> {
  const isForm = opt.body instanceof FormData;
  const r = await fetch(`/api/v1/public/app/${path}`, {
    method: opt.method ?? (opt.body ? 'POST' : 'GET'),
    headers: { ...(isForm || !opt.body ? {} : { 'Content-Type': 'application/json' }), ...(tok.get() ? { Authorization: `Bearer ${tok.get()}` } : {}) },
    body: opt.body ? (isForm ? opt.body : JSON.stringify(opt.body)) : undefined,
  });
  if (r.status === 401) { tok.set(null); location.reload(); }
  const j = r.headers.get('content-type')?.includes('json') ? await r.json() : null;
  if (!r.ok) throw new Error(j?.message ? (Array.isArray(j.message) ? j.message.join('; ') : j.message) : `Ошибка ${r.status}`);
  return j as T;
}

async function fileDownload(id: string, name: string) {
  const r = await fetch(`/api/v1/public/app/documents/${id}/file`, { headers: { Authorization: `Bearer ${tok.get()}` } });
  if (!r.ok) { alert('Не удалось скачать файл'); return; }
  const url = URL.createObjectURL(await r.blob());
  Object.assign(document.createElement('a'), { href: url, download: name }).click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const btn = 'rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50';
const inp = 'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base outline-none focus:border-brand-500';

export function MobileAppPage() {
  const brand = useBranding();
  const [logged, setLogged] = useState(!!tok.get());
  const [tab, setTab] = useState<'chat' | 'docs' | 'orders' | 'kb' | 'me'>('chat');
  useEffect(() => {
    document.title = brand.appName;
    let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (!link) { link = Object.assign(document.createElement('link'), { rel: 'manifest' }); document.head.appendChild(link); }
    link.href = '/api/v1/public/manifest/app';
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  }, [brand.appName]);
  if (!logged) return <AppLogin name={brand.appName} logo={brand.logo} onDone={() => setLogged(true)} />;
  const tabs: [typeof tab, string, ReactNode][] = [['chat', 'Помощник', <MessageCircle key="c" size={20} />], ['docs', 'Документы', <FileText key="d" size={20} />], ['orders', 'Заказы', <ShoppingBag key="o" size={20} />], ['kb', 'Справочник', <BookOpen key="k" size={20} />], ['me', 'Профиль', <User key="m" size={20} />]];
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="sticky top-0 z-10 flex items-center gap-2 bg-brand-600 px-4 py-3 text-white">{brand.logo && <img src={brand.logo} alt="" className="h-7 w-7 rounded bg-white object-contain" />}<b>{brand.appName}</b></header>
      <main className="mx-auto w-full max-w-xl flex-1 p-4 pb-24">
        {tab === 'chat' && <AppChat />}
        {tab === 'docs' && <AppDocs />}
        {tab === 'orders' && <AppOrders />}
        {tab === 'kb' && <AppKb />}
        {tab === 'me' && <AppProfile onLogout={() => { tok.set(null); setLogged(false); }} />}
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-10 grid grid-cols-5 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)]">
        {tabs.map(([id, l, icon]) => <button key={id} onClick={() => setTab(id)} className={`flex flex-col items-center gap-0.5 py-2 text-[11px] ${tab === id ? 'text-brand-700' : 'text-slate-500'}`}>{icon}{l}</button>)}
      </nav>
    </div>
  );
}

function AppLogin({ name, logo, onDone }: { name: string; logo: string | null; onDone: () => void }) {
  const [phone, setPhone] = useState('+7');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(false);
  const [demo, setDemo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async (e: FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { const r = await call('code', { body: { phone, consent: true } }); setSent(true); setDemo(r.demoCode ?? null); } catch (x: any) { setErr(x.message); }
    setBusy(false);
  };
  const login = async (e: FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { const r = await call('login', { body: { phone, code } }); tok.set(r.token); onDone(); } catch (x: any) { setErr(x.message); }
    setBusy(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <form onSubmit={sent ? login : send} className="w-full max-w-sm space-y-4 rounded-xl bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">{logo && <img src={logo} alt="" className="h-10 w-10 object-contain" />}<div><div className="text-lg font-semibold">{name}</div><div className="text-xs text-slate-500">Юридическая помощь в телефоне</div></div></div>
        <label className="block text-sm">Номер телефона<input className={inp} type="tel" inputMode="tel" autoComplete="tel" value={phone} disabled={sent} onChange={(e) => setPhone(e.target.value)} /></label>
        {!sent && <label className="flex items-start gap-2 text-xs text-slate-600"><input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />Даю согласие на обработку персональных данных (номер телефона, сведения из обращений) для оказания юридических услуг и связи со мной (152-ФЗ).</label>}
        {sent && <label className="block text-sm">Код из SMS<input className={inp} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></label>}
        {demo && <div className="rounded bg-amber-50 p-2 text-xs text-amber-800">Демонстрационный режим, SMS не отправляется. Код: <b>{demo}</b></div>}
        {err && <div className="text-sm text-red-600">{err}</div>}
        <button className={`${btn} w-full`} disabled={busy || (!sent && (!consent || phone.replace(/\D/g, '').length < 11)) || (sent && code.length !== 6)}>{sent ? 'Войти' : 'Получить код'}</button>
        {sent && <button type="button" className="w-full text-xs text-slate-500 underline" onClick={() => { setSent(false); setCode(''); }}>Изменить номер</button>}
      </form>
    </div>
  );
}

function AppChat() {
  const [msgs, setMsgs] = useState<{ role: string; text: string }[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { call('chat').then(setMsgs).catch(() => undefined); }, []);
  useEffect(() => end.current?.scrollIntoView({ behavior: 'smooth' }), [msgs]);
  const send = async (e: FormEvent) => {
    e.preventDefault(); if (!text.trim()) return;
    const t = text; setText(''); setErr(null); setBusy(true); setMsgs((m) => [...m, { role: 'user', text: t }]);
    try { const r = await call('chat', { body: { text: t } }); setMsgs((m) => [...m, { role: 'bot', text: r.reply }]); } catch (x: any) { setErr(x.message); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      {!msgs.length && <div className="rounded-lg bg-white p-4 text-sm text-slate-600">Опишите ситуацию своими словами. Помощник задаст уточняющие вопросы и подскажет варианты действий. Ответы носят справочный характер, для точной оценки закажите консультацию юриста.</div>}
      {msgs.map((m, i) => <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : ''}`}><div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-brand-600 text-white' : 'bg-white'}`}>{m.text}</div></div>)}
      {busy && <div className="text-sm text-slate-400">Помощник печатает...</div>}
      {err && <div className="text-sm text-red-600">{err}</div>}
      <div ref={end} />
      <form onSubmit={send} className="fixed inset-x-0 bottom-14 mx-auto flex max-w-xl gap-2 bg-slate-50 p-3"><input className={inp} placeholder="Ваш вопрос" value={text} onChange={(e) => setText(e.target.value)} /><button className={btn} disabled={busy} aria-label="Отправить"><Send size={18} /></button></form>
    </div>
  );
}

function AppDocs() {
  const [tpl, setTpl] = useState<any[]>([]);
  const [sel, setSel] = useState<any | null>(null);
  const [ans, setAns] = useState<Record<string, string>>({});
  const [res, setRes] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { call('templates').then(setTpl).catch((e) => setErr(e.message)); }, []);
  const make = async () => {
    setBusy(true); setErr(null);
    try { setRes(await call('documents', { body: { template: sel.code, answers: ans } })); } catch (x: any) { setErr(x.message); }
    setBusy(false);
  };
  if (res) return (
    <div className="space-y-3 rounded-lg bg-white p-4">
      <div className="font-semibold">{sel.title}</div>
      <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-sm">{res.text}</div>
      <div className="text-xs text-slate-500">Проверьте текст и впишите недостающие сведения вместо пропусков. Документ сохранён в разделе «Заказы».</div>
      <div className="flex gap-2"><button className={btn} onClick={() => fileDownload(res.documentId, `${sel.title}.docx`)}>Скачать DOCX</button><button className="text-sm text-slate-600 underline" onClick={() => { setRes(null); setSel(null); setAns({}); }}>Готово</button></div>
    </div>
  );
  if (sel) return (
    <div className="space-y-3 rounded-lg bg-white p-4">
      <button className="text-sm text-slate-500" onClick={() => setSel(null)}>Назад</button>
      <div className="font-semibold">{sel.title}</div>
      {sel.fields.map((f: any) => <label key={f.key} className="block text-sm">{f.label}{f.multiline ? <textarea rows={4} className={inp} placeholder={f.hint} value={ans[f.key] ?? ''} onChange={(e) => setAns({ ...ans, [f.key]: e.target.value })} /> : <input className={inp} placeholder={f.hint} value={ans[f.key] ?? ''} onChange={(e) => setAns({ ...ans, [f.key]: e.target.value })} />}</label>)}
      {err && <div className="text-sm text-red-600">{err}</div>}
      <button className={`${btn} w-full`} disabled={busy} onClick={make}>{busy ? 'Составляю документ...' : 'Составить документ'}</button>
    </div>
  );
  return (
    <div className="space-y-2">
      <div className="text-sm text-slate-600">Выберите документ, ответьте на вопросы, и приложение составит его за вас.</div>
      {err && <div className="text-sm text-red-600">{err}</div>}
      {tpl.map((t) => <button key={t.code} onClick={() => setSel(t)} className="block w-full rounded-lg bg-white p-4 text-left"><div className="text-xs text-slate-500">{t.category}</div><div className="font-medium">{t.title}</div></button>)}
    </div>
  );
}

const KIND: Record<string, string> = { QUESTION: 'Вопрос юристу', REVIEW: 'Проверка документа', REPRESENT: 'Представительство', DOCUMENT: 'Документ' };
const ST: Record<string, string> = { NEW: 'принят', AWAITING_PAYMENT: 'ожидает оплаты', IN_WORK: 'в работе', DONE: 'выполнен', CANCELLED: 'отменён' };

function AppOrders() {
  const [list, setList] = useState<any[]>([]);
  const [svc, setSvc] = useState<any>(null);
  const [mode, setMode] = useState<'list' | 'new' | string>('list');
  const load = () => call('orders').then(setList).catch(() => undefined);
  useEffect(() => { load(); call('services').then(setSvc).catch(() => undefined); }, []);
  if (mode === 'new') return <NewOrder svc={svc} onDone={() => { setMode('list'); load(); }} />;
  if (mode !== 'list') return <OrderView id={mode} onBack={() => { setMode('list'); load(); }} />;
  return (
    <div className="space-y-2">
      <button className={`${btn} w-full`} onClick={() => setMode('new')}>Обратиться к юристу</button>
      {list.map((o) => <button key={o.id} onClick={() => setMode(o.id)} className="block w-full rounded-lg bg-white p-4 text-left"><div className="flex justify-between text-xs text-slate-500"><span>{KIND[o.kind]}</span><span>{ST[o.status]}</span></div><div className="font-medium">{o.title}</div></button>)}
      {!list.length && <div className="text-center text-sm text-slate-500">Заказов пока нет</div>}
    </div>
  );
}

function NewOrder({ svc, onDone }: { svc: any; onDone: () => void }) {
  const [f, setF] = useState({ kind: 'QUESTION', title: '', description: '' });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const fd = new FormData(); Object.entries(f).forEach(([k, v]) => v && fd.append(k, v)); fd.append('returnUrl', `${location.origin}/app`); if (file) fd.append('file', file);
      const r = await call('orders', { body: fd });
      if (r.payUrl) { location.href = r.payUrl; return; }
      onDone();
    } catch (x: any) { setErr(x.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg bg-white p-4">
      <button type="button" className="text-sm text-slate-500" onClick={onDone}>Назад</button>
      <div className="grid grid-cols-3 gap-2">{['QUESTION', 'REVIEW', 'REPRESENT'].map((k) => <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} className={`rounded-lg border p-2 text-xs ${f.kind === k ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-slate-200'}`}>{KIND[k]}</button>)}</div>
      {f.kind === 'REVIEW' && svc && <div className="text-sm">Стоимость проверки: <b>{Number(svc.reviewPrice).toLocaleString('ru-RU')} руб.</b>{svc.payOnline ? ' Оплата картой или СБП через ЮKassa.' : ' Юрист свяжется для оплаты.'}</div>}
      {f.kind !== 'REVIEW' && <div className="text-xs text-slate-500">Юрист изучит обращение и сообщит стоимость, если вопрос потребует платной работы.</div>}
      <label className="block text-sm">Тема<input className={inp} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></label>
      <label className="block text-sm">Опишите ситуацию<textarea rows={5} className={inp} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
      <label className="block text-sm">{f.kind === 'REVIEW' ? 'Документ для проверки' : 'Файл (необязательно)'}<input type="file" accept="image/*,application/pdf,.doc,.docx" capture={undefined} onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
      {err && <div className="text-sm text-red-600">{err}</div>}
      <button className={`${btn} w-full`} disabled={busy || f.title.length < 3 || (f.kind === 'REVIEW' && !file)}>{f.kind === 'REVIEW' && svc?.payOnline ? 'Перейти к оплате' : 'Отправить'}</button>
    </form>
  );
}

function OrderView({ id, onBack }: { id: string; onBack: () => void }) {
  const [o, setO] = useState<any>(null);
  const [text, setText] = useState('');
  const load = () => call(`orders/${id}`).then(setO).catch(() => undefined);
  useEffect(() => { load(); }, [id]);
  const send = async (e: FormEvent) => { e.preventDefault(); if (!text.trim()) return; await call(`orders/${id}/messages`, { body: { text } }); setText(''); load(); };
  if (!o) return null;
  return (
    <div className="space-y-3 rounded-lg bg-white p-4">
      <button className="text-sm text-slate-500" onClick={onBack}>Назад</button>
      <div className="text-xs text-slate-500">{KIND[o.kind]} · {ST[o.status]}{o.price ? ` · ${Number(o.price).toLocaleString('ru-RU')} руб.${o.paid ? ', оплачено' : ''}` : ''}</div>
      <div className="font-semibold">{o.title}</div>
      {o.description && <div className="whitespace-pre-wrap text-sm text-slate-700">{o.description}</div>}
      {o.resultComment && <div className="whitespace-pre-wrap rounded bg-brand-50 p-3 text-sm">{o.resultComment}</div>}
      {o.resultDocumentId && <button className={btn} onClick={() => fileDownload(o.resultDocumentId, `${o.title}.docx`)}>Скачать результат</button>}
      {o.kind !== 'DOCUMENT' && <>
        <div className="space-y-1">{(o.chat ?? []).map((m: any, i: number) => <div key={i} className={`flex ${m.from === 'client' ? 'justify-end' : ''}`}><div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.from === 'client' ? 'bg-brand-600 text-white' : 'bg-slate-100'}`}>{m.text}</div></div>)}</div>
        <form onSubmit={send} className="flex gap-2"><input className={inp} placeholder="Сообщение юристу" value={text} onChange={(e) => setText(e.target.value)} /><button className={btn} aria-label="Отправить"><Send size={18} /></button></form>
      </>}
    </div>
  );
}

function AppKb() {
  const [list, setList] = useState<any[]>([]);
  const [a, setA] = useState<any>(null);
  useEffect(() => { call('articles').then(setList).catch(() => undefined); }, []);
  if (a) return <div className="space-y-3 rounded-lg bg-white p-4"><button className="text-sm text-slate-500" onClick={() => setA(null)}>Назад</button><div className="text-xs text-slate-500">{a.category}</div><h1 className="text-lg font-semibold">{a.title}</h1><div className="whitespace-pre-wrap text-sm leading-relaxed">{a.body}</div></div>;
  return <div className="space-y-2">{list.map((x) => <button key={x.id} onClick={() => call(`articles/${x.id}`).then(setA)} className="block w-full rounded-lg bg-white p-4 text-left"><div className="text-xs text-slate-500">{x.category}</div><div className="font-medium">{x.title}</div><div className="mt-1 line-clamp-2 text-xs text-slate-600">{x.excerpt}</div></button>)}</div>;
}

function AppProfile({ onLogout }: { onLogout: () => void }) {
  const [p, setP] = useState<any>(null);
  const [ok, setOk] = useState(false);
  useEffect(() => { call('me').then(setP).catch(() => undefined); }, []);
  if (!p) return null;
  const save = async () => { await call('me', { method: 'PATCH', body: { name: p.name || undefined, email: p.email || undefined } }); setOk(true); };
  return (
    <div className="space-y-3 rounded-lg bg-white p-4">
      <div className="text-sm text-slate-500">Телефон: {p.phone}</div>
      <label className="block text-sm">ФИО<input className={inp} value={p.name ?? ''} onChange={(e) => { setP({ ...p, name: e.target.value }); setOk(false); }} /></label>
      <label className="block text-sm">Электронная почта<input className={inp} type="email" value={p.email ?? ''} onChange={(e) => { setP({ ...p, email: e.target.value }); setOk(false); }} /></label>
      <button className={btn} onClick={save}>{ok ? 'Сохранено' : 'Сохранить'}</button>
      <button className="block text-sm text-slate-500 underline" onClick={onLogout}>Выйти</button>
    </div>
  );
}
